import { ComponentType, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type DeferredSectionProps = {
  loader: () => Promise<{ default: ComponentType }>;
  placeholderClassName?: string;
  rootMargin?: string;
};

export default function DeferredSection({
  loader,
  placeholderClassName,
  rootMargin = "500px 0px",
}: DeferredSectionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [Component, setComponent] = useState<ComponentType | null>(null);

  useEffect(() => {
    if (Component) return;

    let cancelled = false;
    const node = containerRef.current;
    if (!node) return;

    const load = async () => {
      const module = await loader();
      if (!cancelled) {
        setComponent(() => module.default);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          void load();
        }
      },
      { rootMargin, threshold: 0.01 },
    );

    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [Component, loader, rootMargin]);

  return (
    <div ref={containerRef}>
      {Component ? (
        <Component />
      ) : (
        <div
          aria-hidden="true"
          className={cn(
            "min-h-[420px] w-full border-y border-transparent bg-transparent",
            placeholderClassName,
          )}
        />
      )}
    </div>
  );
}
